import Box from "@mui/material/Box";
import { useEffect, useMemo, useState } from "react";

import { money, whatIfSetup } from "@/libs/tournamentSetupApi";
import type { EventPatch, FormatKey, SetupEventView, TournamentSetupView } from "@/libs/tournamentSetupApi";

import { formatDay } from "../setup/fields";
import { DenseButton, DenseInput, DenseSelect, SetupCard, tone } from "../setup/ui";
import { useSetupCommand } from "./use-setup-command";

const KEEP = "__keep__";
const LADDER = [8, 12, 16, 24, 32, 48, 64];
const FORMAT_LABEL: Record<FormatKey, string> = {
  knockout: "Knockout",
  knockout_consolation: "Knockout + consolation",
  groups_knockout: "Round-robin groups → knockout",
  round_robin: "Round robin",
  double_elimination: "Double elimination",
  places_playout: "Places playout",
  guaranteed_minimum: "Guaranteed minimum matches",
};
const SCORING: Record<string, string> = {
  "21x3": "Games to 21, best of 3",
  "15x3": "Games to 15, best of 3",
  "11x5": "Games to 11, best of 5",
  "21x1": "One game to 21",
};

type Field = "fee" | "limit" | "format" | "day" | "scoring" | "when_full" | "minimum" | "rest";

function distinct<T>(values: T[]): T[] {
  return [...new Set(values.map((v) => JSON.stringify(v)))].map((v) => JSON.parse(v) as T);
}

function Row({ label, htmlFor, children, caption, changing }: { label: string; htmlFor: string; children: React.ReactNode; caption?: string; changing?: boolean }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "10px", alignItems: "center", py: "7px", borderBottom: `1px solid ${tone.divider}` }}>
      <Box component="label" htmlFor={htmlFor} sx={{ fontSize: 12, fontWeight: 600, color: tone.muted }}>
        {label}
      </Box>
      <Box sx={changing ? { "& select, & input": { borderColor: `${tone.brand} !important`, boxShadow: `0 0 0 3px ${tone.brandRing}` } } : undefined}>
        {children}
        {caption ? (
          <Box sx={{ fontSize: 11.5, mt: "3px", color: changing ? tone.brandText : tone.subtle, fontWeight: changing ? 600 : 400 }}>{caption}</Box>
        ) : null}
      </Box>
    </Box>
  );
}

/** Edit many events at once (F7): each field says "same on all", "keep each
 * event's …" or "changing: was …", and Apply sends only the changed fields.
 * The court-time line is the server's what-if. */
export function BulkEditPanel({
  view,
  events,
  onViewChange,
  onDone,
}: {
  view: TournamentSetupView;
  events: SetupEventView[];
  onViewChange: (next: TournamentSetupView) => void;
  onDone: () => void;
}) {
  const { pending, error, run } = useSetupCommand(view, onViewChange);
  const n = events.length;
  const pairs = events.every((e) => e.entry_unit === "pairs");
  const singles = events.every((e) => e.entry_unit === "players");
  const current = useMemo(
    () => ({
      fee: distinct(events.map((e) => Number.parseFloat(String(e.entry_fee)).toFixed(2))),
      limit: distinct(events.map((e) => e.entry_limit)),
      format: distinct(events.map((e) => e.format)),
      day: distinct(events.map((e) => e.stage_days[0].play_date)),
      scoring: distinct(events.map((e) => e.rules.scoring)),
      when_full: distinct(events.map((e) => e.rules.when_full)),
      minimum: distinct(events.map((e) => e.rules.minimum_entries)),
      rest: distinct(events.map((e) => e.rules.rest_minutes ?? null)),
    }),
    [events],
  );
  const initial = (field: Field): string => (current[field].length === 1 ? String(current[field][0] ?? "") : KEEP);
  const [values, setValues] = useState<Record<Field, string>>(() => ({
    fee: initial("fee"), limit: KEEP, format: KEEP, day: KEEP, scoring: initial("scoring"),
    when_full: initial("when_full"), minimum: initial("minimum"), rest: initial("rest"),
  }));
  useEffect(() => {
    setValues({
      fee: initial("fee"), limit: KEEP, format: KEEP, day: KEEP, scoring: initial("scoring"),
      when_full: initial("when_full"), minimum: initial("minimum"), rest: initial("rest"),
    });
    // reset when the selection changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.map((e) => e.event_id).join()]);
  const changed = (field: Field) => values[field] !== KEEP && values[field] !== initial(field);
  const caption = (field: Field, describe: (v: string) => string, keepLabel?: string) => {
    if (changed(field)) {
      return current[field].length === 1
        ? `Changing: was ${describe(String(current[field][0] ?? ""))}${n > 1 ? ` on all ${n}` : ""}`
        : `Changing: was ${current[field].length} different values`;
    }
    if (values[field] === KEEP) return keepLabel;
    return `Same on all ${n} · unchanged`;
  };
  const unit = singles ? "players" : pairs ? "pairs" : "entries";
  const patch: EventPatch = {};
  const rules: Record<string, unknown> = {};
  if (changed("fee")) patch.entry_fee = money(Number.parseFloat(values.fee || "0").toFixed(2));
  if (changed("limit")) patch.entry_limit = Number(values.limit);
  if (changed("format")) patch.format = values.format as FormatKey;
  if (changed("day")) patch.stage_days = [{ stage: "all", play_date: values.day }];
  if (changed("scoring")) rules.scoring = values.scoring;
  if (changed("when_full")) rules.when_full = values.when_full;
  if (changed("minimum")) rules.minimum_entries = Number(values.minimum);
  if (changed("rest")) rules.rest_minutes = values.rest ? Number(values.rest) : null;
  if (Object.keys(rules).length) patch.rules = rules as EventPatch["rules"];
  const changeCount = Object.keys(patch).length - (patch.rules ? 1 : 0) + Object.keys(rules).length;

  const timeChange = changed("limit") || changed("format") || changed("day");
  const [whatIf, setWhatIf] = useState<string | null>(null);
  const changeKey = JSON.stringify([values.limit, values.format, values.day]);
  useEffect(() => {
    if (!timeChange) {
      setWhatIf(null);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      void whatIfSetup(view.tournament_id, {
        changes: [
          {
            targets: events.map((e) => ({ event_id: e.event_id })),
            entry_limit: changed("limit") ? Number(values.limit) : null,
            format: changed("format") ? (values.format as FormatKey) : null,
            stage_days: changed("day") ? [{ stage: "all", play_date: values.day }] : null,
          },
        ],
      })
        .then((result) => {
          if (active) setWhatIf(result.summary_line);
        })
        .catch(() => {
          if (active) setWhatIf(null);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeKey, timeChange]);
  const sameLine = `No change to court time: ${view.time_check.days.map((d) => `${d.label} ${d.percent_label}%`).join(", ")}.`;
  const set = (field: Field) => (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) =>
    setValues((v) => ({ ...v, [field]: event.target.value }));
  const kinds = distinct(events.map((e) => e.kind_label));

  return (
    <SetupCard
      extra={pairs ? (kinds.length > 1 ? "All doubles events" : `All ${kinds[0].toLowerCase()}`) : `${n} events`}
      sx={{ width: { xs: "100%", md: 480 }, flex: "none", display: "flex", flexDirection: "column", overflow: "hidden" }}
      title={`Edit ${n} selected`}
    >
      <Box sx={{ p: "6px 14px", display: "flex", flexDirection: "column" }}>
        <Row caption={caption("fee", (v) => `$${v}`, "Keep each event’s fee")} changing={changed("fee")} htmlFor="b-fee" label="Fee per entry">
          <DenseInput
            id="b-fee"
            inputMode="decimal"
            mono
            // A cleared fee keeps each event's fee; it never becomes a free event.
            onChange={(event) => {
              const fee = event.target.value.replace(/[^0-9.]/g, "");
              setValues((v) => ({ ...v, fee: fee || KEEP }));
            }}
            placeholder="Keep each event’s fee"
            value={values.fee === KEEP ? "" : `$${values.fee}`}
          />
        </Row>
        <Row caption={caption("limit", (v) => `${v} ${unit}`)} changing={changed("limit")} htmlFor="b-lim" label="Entry limit">
          <DenseSelect id="b-lim" onChange={set("limit")} value={values.limit}>
            <option value={KEEP}>Keep each event’s limit ({current.limit.join(" or ")} {unit})</option>
            {LADDER.map((size) => (
              <option key={size} value={size}>
                {size} {unit}
              </option>
            ))}
          </DenseSelect>
        </Row>
        <Row caption={caption("format", (v) => FORMAT_LABEL[v as FormatKey])} changing={changed("format")} htmlFor="b-fmt" label="Format">
          <DenseSelect id="b-fmt" onChange={set("format")} value={values.format}>
            <option value={KEEP}>
              Keep each event’s format ({current.format.length} kind{current.format.length === 1 ? "" : "s"})
            </option>
            {(Object.keys(FORMAT_LABEL) as FormatKey[]).map((key) => (
              <option key={key} value={key}>
                {FORMAT_LABEL[key]}
              </option>
            ))}
          </DenseSelect>
        </Row>
        <Row caption={caption("day", (v) => formatDay(v))} changing={changed("day")} htmlFor="b-day" label="Day">
          <DenseSelect id="b-day" onChange={set("day")} value={values.day}>
            <option value={KEEP}>Keep each event’s day ({current.day.map((d) => formatDay(d).split(",")[0]).join(" or ")})</option>
            {view.days.map((day) => (
              <option key={day.play_date} value={day.play_date}>
                {formatDay(day.play_date)}
              </option>
            ))}
          </DenseSelect>
        </Row>
        <Row caption={caption("scoring", (v) => SCORING[v])} changing={changed("scoring")} htmlFor="b-sc" label="Scoring">
          <DenseSelect id="b-sc" onChange={set("scoring")} value={values.scoring}>
            {values.scoring === KEEP ? <option value={KEEP}>Keep each event’s scoring</option> : null}
            {Object.entries(SCORING).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </DenseSelect>
        </Row>
        <Row caption={caption("when_full", (v) => (v === "waitlist" ? "Waitlist" : "Close entries"))} changing={changed("when_full")} htmlFor="b-full" label="When full">
          <DenseSelect id="b-full" onChange={set("when_full")} value={values.when_full}>
            {values.when_full === KEEP ? <option value={KEEP}>Keep each event’s rule</option> : null}
            <option value="waitlist">Waitlist</option>
            <option value="close">Close entries</option>
          </DenseSelect>
        </Row>
        <Row caption={caption("minimum", (v) => `${v} ${unit}`)} changing={changed("minimum")} htmlFor="b-min" label="Minimum to run">
          <DenseSelect id="b-min" onChange={set("minimum")} value={values.minimum}>
            {values.minimum === KEEP ? <option value={KEEP}>Keep each event’s minimum</option> : null}
            {[2, 3, 4, 6, 8, 12, 16].map((v) => (
              <option key={v} value={v}>
                {v} {unit}
              </option>
            ))}
          </DenseSelect>
        </Row>
        <Row caption={caption("rest", (v) => (v ? `${v} min` : "the tournament default"))} changing={changed("rest")} htmlFor="b-rest" label="Minimum rest">
          <DenseSelect id="b-rest" onChange={set("rest")} value={values.rest}>
            {values.rest === KEEP ? <option value={KEEP}>Keep each event’s rest</option> : null}
            <option value="">Tournament default ({view.time.minimum_rest_minutes} min)</option>
            {[10, 15, 20, 30, 45, 60].map((v) => (
              <option key={v} value={v}>
                {v} min
              </option>
            ))}
          </DenseSelect>
        </Row>
      </Box>
      <Box sx={{ m: "6px 14px", p: "8px 10px", borderRadius: "8px", bgcolor: tone.page, border: `1px solid ${tone.divider}`, fontSize: 12.5, color: tone.ink2 }}>
        {changeCount ? `Changes ${changeCount} setting${changeCount === 1 ? "" : "s"} on ${n} events. ` : "Nothing changed yet. "}
        <b>{timeChange ? whatIf ?? "Checking court time…" : sameLine}</b>
      </Box>
      {error ? (
        <Box role="alert" sx={{ m: "0 14px 6px", fontSize: 12.5, color: tone.badText }}>
          {error}
        </Box>
      ) : null}
      <Box sx={{ mt: "auto", display: "flex", gap: 1, p: "12px 14px", borderTop: `1px solid ${tone.divider}`, bgcolor: tone.page }}>
        <Box sx={{ flexGrow: 1 }} />
        <DenseButton onClick={onDone}>Cancel</DenseButton>
        <DenseButton
          disabled={pending || !changeCount}
          onClick={async () => {
            if (await run({ command: "update_events", event_ids: events.map((e) => e.event_id), patch } as never)) onDone();
          }}
          variant="primary"
        >
          Apply to {n} events
        </DenseButton>
      </Box>
    </SetupCard>
  );
}
